import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion'
import {
  Activity,
  Brain,
  Database,
  Users,
  TrendingUp,
  Shield,
  Globe,
  ArrowRight,
  ExternalLink,
  Heart,
  Thermometer,
  Wind,
  ChevronDown,
  ChevronUp,
  MapPin,
  Droplets,
  Leaf,
  Factory,
  Target,
  AlertTriangle,
  Mail,
  Smartphone,
  Monitor
} from 'lucide-react'

import { TypewriterEffectSmooth } from '../components/ui/TypewriterEffect'
import AnimatedMetrics from '../components/ui/AnimatedMetrics'
import LandingNavbar from '../components/ui/LandingNavbar'
import TeamMemberModal from '../components/ui/TeamMemberModal'
import { landingAPI, landingContentAPI, teamAPI } from '../services/api'
import { cn } from '../utils/cn'
import { resolveTeamCardSlots, resolveTeamSectionLayout, truncateBioExcerpt } from './teamCardLogic.mjs'
import { resolveDiseaseCopy, isSafeHttpUrl } from './landingContentDefaults'
import { getHeroScrollRanges, HERO_SCROLL_INPUT_RANGE } from './heroScrollMotion.mjs'
import { resolveImageOverride, resolveImageOverridesSource, resolveSectionImage } from './imageOverrideLogic.mjs'

// Shared section header: eyebrow-free (no invented labels), just a
// consistent typographic treatment — title, a small brand-gradient accent
// bar, and an optional subtitle — reused by every section below instead of
// each one re-declaring its own one-off heading markup. Content in, content
// out: callers still supply the exact same title/subtitle strings they
// always did.
const SectionHeading = ({ title, subtitle, maxWidthClass = 'max-w-3xl', className }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
    viewport={{ once: true }}
    className={cn('mx-auto mb-14 text-center md:mb-16', maxWidthClass, className)}
  >
    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
      {title}
    </h2>
    <span className="mx-auto mt-5 block h-1 w-14 rounded-full bg-brand-gradient" aria-hidden="true" />
    {subtitle && (
      <p className="mt-5 text-lg leading-relaxed text-gray-600 md:text-xl">
        {subtitle}
      </p>
    )}
  </motion.div>
)

// Tier 1 of the "Expert Team" section — members with a biography (see
// teamCardLogic.mjs's splitTeamByTier). Larger photo, project role,
// institution, and a truncated bio excerpt with a "View full profile"
// affordance; clicking (or Enter/Space, since this is a real <button>)
// opens TeamMemberModal with the complete profile. These carry the
// section's credibility argument, so they're visually the most prominent
// cards in the section.
//
// Sized as a fixed-width flex item (not a grid cell) inside a
// `flex flex-wrap justify-center` container in LandingPage: the browser
// wraps as many cards fit per row for the current viewport, and
// `justify-center` centers whatever count lands on the final row — so a
// ragged last row never happens regardless of how many members end up in
// this tier (4 today, potentially all 11 once every write-up lands).
//
// The `sm:w-72` (288px) is load-bearing, not an arbitrary pick. This
// section's container is max-w-7xl (1280px) less px-4 on either side,
// leaving 1248px usable, so four cards plus three 24px gaps have to fit
// inside 1248. At the 300px this was first built with, four came to 1272
// and the fourth card wrapped alone underneath a row of three — the exact
// ragged row this layout exists to prevent, just moved up a tier. 288px
// totals 1224 and lands all four on one row with room to spare.
const FeaturedTeamCard = ({ member, index, prefersReducedMotion, onSelect }) => {
  const cardSlots = resolveTeamCardSlots(member)
  const bioExcerpt = truncateBioExcerpt(member.bio)
  const motionProps = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
        viewport: { once: true },
        transition: { duration: 0.3 },
      }
    : {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.6, delay: index * 0.08 },
        whileHover: { y: -6, scale: 1.02 },
      }

  return (
    <motion.button
      type="button"
      {...motionProps}
      className="group flex w-full flex-col items-center rounded-2xl border border-gray-200/80 bg-white p-6 text-center shadow-elev-1 transition-all duration-300 hover:shadow-elev-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 sm:w-72"
      onClick={(e) => onSelect(member, e)}
    >
      <div className="mb-4 h-24 w-24 overflow-hidden rounded-full ring-4 ring-primary-400/30 transition-all duration-300 group-hover:ring-primary-400/60">
        <div className="flex h-full w-full items-center justify-center bg-gray-200">
          {member.image ? (
            <img
              src={member.image}
              alt={member.name}
              className="h-full w-full object-cover"
              width={96}
              height={96}
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextElementSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <span
            className="flex h-full w-full items-center justify-center text-sm text-gray-500"
            style={{ display: member.image ? 'none' : 'flex' }}
          >
            Photo
          </span>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
      {cardSlots.hasRole && (
        <p className="mt-1 text-sm font-medium text-primary-600">{member.role}</p>
      )}
      {cardSlots.hasAffiliation && (
        <p className="mt-0.5 truncate max-w-[220px] text-xs text-gray-500">
          {member.institutionShortName || member.affiliation}
        </p>
      )}
      {bioExcerpt && (
        <p className="mt-3 text-sm leading-relaxed text-gray-600">{bioExcerpt}</p>
      )}

      <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 transition-colors group-hover:text-primary-700">
        View full profile
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </motion.button>
  )
}

// Tier 2 of the "Expert Team" section — everyone without a biography yet
// (photo, name, institution, and role only when the data has one).
// Deliberately quieter than the featured tier — smaller photo, less
// chrome, no bio/affordance — so the missing write-up reads as a density
// choice, not a broken card. Same fixed-width-flex-item +
// `justify-center` wrapping strategy as FeaturedTeamCard, just at a
// smaller card size, which is what keeps the two tiers reading as one
// component family rather than two bolted-together designs.
const CompactTeamCard = ({ member, index, prefersReducedMotion, onSelect }) => {
  const cardSlots = resolveTeamCardSlots(member)
  const motionProps = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
        viewport: { once: true },
        transition: { duration: 0.3 },
      }
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.5, delay: index * 0.04 },
        whileHover: { y: -3 },
      }

  return (
    <motion.button
      type="button"
      {...motionProps}
      className="group flex w-32 flex-col items-center rounded-xl border border-gray-200/60 bg-gray-50 p-3 text-center shadow-elev-1 transition-all duration-300 hover:bg-white hover:shadow-elev-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 sm:w-36"
      onClick={(e) => onSelect(member, e)}
    >
      <div className="mb-2 h-14 w-14 overflow-hidden rounded-full ring-2 ring-primary-400/20 transition-all duration-300 group-hover:ring-primary-400/50">
        <div className="flex h-full w-full items-center justify-center bg-gray-200">
          {member.image ? (
            <img
              src={member.image}
              alt={member.name}
              className="h-full w-full object-cover"
              width={56}
              height={56}
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextElementSibling.style.display = 'flex'
              }}
            />
          ) : null}
          <span
            className="flex h-full w-full items-center justify-center text-[10px] text-gray-500"
            style={{ display: member.image ? 'none' : 'flex' }}
          >
            Photo
          </span>
        </div>
      </div>

      <h4 className="w-full truncate text-xs font-semibold text-gray-800">{member.name}</h4>
      {cardSlots.hasAffiliation && (
        <p className="mt-0.5 w-full truncate text-[11px] text-gray-500">
          {member.institutionShortName || member.affiliation}
        </p>
      )}
      {cardSlots.hasRole && (
        <p className="w-full truncate text-[11px] text-gray-400">{member.role}</p>
      )}
    </motion.button>
  )
}

// Climate Data Section — expandable categories showing 30 climate variables
const ClimateDataSection = ({ content }) => {
  const [expandedCategory, setExpandedCategory] = useState(null)

  const climateCategories = [
    {
      id: 'meteorological',
      title: 'Meteorological Parameters',
      count: 19,
      icon: Thermometer,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      variables: [
        'Minimum Temperature (Tmin)', 'Maximum Temperature (Tmax)',
        'Land Surface Temperature Day', 'Land Surface Temperature Night',
        'Mean Radiant Temperature (MRT)', 'Universal Thermal Climate Index (UTCI)',
        'Rainfall', 'Relative Humidity (RH)', 'Specific Humidity (SH)',
        'Zonal Wind (U)', 'Meridional Wind (V)', 'Vertical Velocity (W)',
        'Convective Available Potential Energy (CAPE)',
        'Surface Latent Heat Flux (SLHF)', 'Surface Sensible Heat Flux (SSHF)',
        'Surface Thermal Radiation Downwards (STRD)',
        'Surface Solar Radiation Downwards (SSRD)',
        'Surface Pressure (SP)', 'Standardised Precipitation-Evapotranspiration Index (SPEI)'
      ]
    },
    {
      id: 'air_pollutants',
      title: 'Air Pollutants',
      count: 6,
      icon: Factory,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-50',
      variables: [
        'Particulate Matter (PM2.5)', 'Carbon Monoxide (CO)',
        'Nitrogen Dioxide (NO2)', 'Ozone (O3)',
        'Sulfur Dioxide (SO2)', 'Formaldehyde (HCHO)'
      ]
    },
    {
      id: 'vegetation_water',
      title: 'Water & Vegetation Health Indices',
      count: 5,
      icon: Leaf,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      variables: [
        'Normalized Difference Water Index (NDWI)',
        'Normalized Difference Vegetation Index (NDVI)',
        'Chlorophyll Index (CI)',
        'Enhanced Vegetation Index (EVI)',
        'Red-Edge Chlorophyll Index (RECI)'
      ]
    }
  ]

  return (
    <section id="climate-data" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeading
          title={content?.climate_data_section?.section_title || 'Climate Related Data'}
          subtitle={content?.climate_data_section?.section_description ||
            '30 climate variables across three categories driving disease prediction models'}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {climateCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              className={`${category.bgColor} rounded-2xl border border-gray-200/80 shadow-elev-1 overflow-hidden transition-all duration-300 hover:shadow-elev-2 hover:-translate-y-0.5`}
            >
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                className="w-full p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                aria-expanded={expandedCategory === category.id}
                aria-controls={`climate-panel-${category.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{category.title}</h3>
                      <p className="text-sm text-gray-500">{category.count} variables</p>
                    </div>
                  </div>
                  {expandedCategory === category.id
                    ? <ChevronUp className="w-5 h-5 text-gray-400" />
                    : <ChevronDown className="w-5 h-5 text-gray-400" />
                  }
                </div>
              </button>

              {expandedCategory === category.id && (
                <motion.div
                  id={`climate-panel-${category.id}`}
                  role="region"
                  aria-label={category.title}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 pb-6"
                >
                  <div className="border-t border-gray-200 pt-4">
                    <ul className="space-y-2">
                      {category.variables.map((variable, vi) => (
                        <li key={vi} className="flex items-center gap-2 text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></span>
                          {variable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const LandingPage = () => {
  const { scrollYProgress } = useScroll()
  const prefersReducedMotion = useReducedMotion()
  // Reduced-motion gate for the hero's scroll-linked fade/shrink — see
  // heroScrollMotion.mjs. Previously these two useTransform calls ran
  // unconditionally, so a prefers-reduced-motion visitor still got the
  // whole hero shrinking/fading as they scrolled, unlike TypewriterEffectSmooth,
  // which already honours the preference.
  const { opacityOutput, scaleOutput } = useMemo(
    () => getHeroScrollRanges(prefersReducedMotion),
    [prefersReducedMotion]
  )
  const opacity = useTransform(scrollYProgress, HERO_SCROLL_INPUT_RANGE, opacityOutput)
  const scale = useTransform(scrollYProgress, HERO_SCROLL_INPUT_RANGE, scaleOutput)

  // State for dynamic data
  // Starts empty, not with seeded numbers. These render as a dash until the
  // real counts arrive from landingAPI.getSystemMetrics(), so the page never
  // shows a fabricated figure — not even for the moment before the API responds.
  const [systemMetrics, setSystemMetrics] = useState({
    models: null,
    users: null,
    alerts: null,
    dataPoints: null
  })
  const [selectedTeamMember, setSelectedTeamMember] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [imageOverrides, setImageOverrides] = useState({ teamImages: {}, partnerLogos: {}, sectionImages: {} })

  // Card that opened the team member modal — focus is restored here on
  // close so keyboard users don't get dropped back at the top of the page.
  const teamModalTriggerRef = useRef(null)

  // Phase A roster (restored 06 June 2026 — Task #12). Loaded from the same
  // /api/public/images call as imageOverrides. The team half of this is
  // unused as of Stage 7 (cleanup) but we keep the partner side for the
  // Partner section. setApiPartnerRoster fires off the same fetch.
  const [apiPartnerRoster, setApiPartnerRoster] = useState(null)

  // Public-team users (Task #32, 06 June 2026 afternoon). When non-empty,
  // this REPLACES the marketing-images.json teamRoster as the source of
  // truth for the team grid: it pulls users from auth_schema.users
  // where show_on_landing = true, with their rich profile baked in
  // (bio, expertise, education, contributions, linkedin, …). Each entry
  // arrives in the same shape the existing UI expects.
  const [apiPublicTeam, setApiPublicTeam] = useState(null)

  // Editable content fetched from /api/public/landing-content.
  // The Settings → Landing Content editor writes here; this fetch makes
  // those edits show up on the public landing page. When the API is
  // unavailable or a key is missing we fall through to the hardcoded
  // copy below so the page never blanks out.
  const [content, setContent] = useState(null)

  // Load system data on component mount
  useEffect(() => {
    const loadSystemData = async () => {
      try {
        const metricsData = await landingAPI.getSystemMetrics()
        setSystemMetrics(metricsData)
      } catch (error) {
        // Leave the counts null so the cards render a dash. There is no mock
        // fallback any more — showing invented figures when the metrics API is
        // down is exactly what this stopped doing.
        console.warn('System metrics unavailable; metric cards will show a dash:', error)
      }
    }

    // Load editable landing content (hero copy, objectives, etc.).
    // Failure here is non-fatal — fallbacks below render the same copy
    // that used to live as hardcoded JSX, so a 500 just looks like
    // "before the editor existed."
    const loadContent = async () => {
      try {
        const r = await landingContentAPI.get()
        if (r?.data) setContent(r.data)
      } catch (err) {
        console.warn('Landing content fetch failed; using hardcoded fallbacks:', err.message)
      }
    }
    loadContent()

    // Load the public team (users with show_on_landing=true). Non-fatal —
    // if it fails or returns empty, falls back to apiTeamRoster from
    // /api/public/images, then to the hardcoded founding list below.
    const loadPublicTeam = async () => {
      try {
        const r = await teamAPI.getPublicTeam()
        if (Array.isArray(r?.team) && r.team.length > 0) setApiPublicTeam(r.team)
      } catch (err) {
        console.warn('Public team fetch failed; falling back to imagesAPI roster:', err.message)
      }
    }
    loadPublicTeam()

    // Load image overrides — API first, falling back to a stale
    // localStorage snapshot only when the API is unreachable, and to an
    // explicitly-empty overrides object (never a blank page) when neither
    // is available. Precedence lives in resolveImageOverridesSource()
    // (imageOverrideLogic.mjs) so it's covered by node --test instead of
    // only implied by this fetch/catch chain — a successful API response
    // is authoritative and is NEVER merged with a stale local snapshot,
    // which is what let a previously-cached device keep showing an old
    // photo/logo/section image after an admin had already saved a fresh
    // one through the editor.
    const loadImageOverrides = async () => {
      let apiOk = false
      let apiData = null
      try {
        const response = await fetch('/api/public/images')
        if (response.ok) {
          const result = await response.json()
          if (result.success && result.data) {
            apiOk = true
            apiData = result.data
            // Phase A roster restore — partner side only as of Stage 7.
            if (Array.isArray(result.data.partnerRoster)) setApiPartnerRoster(result.data.partnerRoster)
          }
        }
      } catch (apiError) {
        console.warn('Failed to load image overrides from API, trying localStorage:', apiError.message)
      }

      let localStorageRaw = null
      if (!apiOk) {
        try {
          localStorageRaw = localStorage.getItem('iewrs_image_overrides')
        } catch (storageError) {
          console.warn('localStorage unavailable for image overrides fallback:', storageError.message)
        }
      }

      setImageOverrides(resolveImageOverridesSource({ apiOk, apiData, localStorageRaw }))
    }

    loadSystemData()
    loadImageOverrides()
  }, [])

  // Typewriter words for hero section — derived from content.hero.title.
  // Words containing certain keywords get the primary accent colour;
  // everything else is gray-800. This preserves the look of the original
  // 6-word hardcoded title while letting the editor change the wording.
  const ACCENT_KEYWORDS = ['Warning', 'Response', 'Predict', 'Diagnos', 'Intelligence', 'IEWRS']
  const HERO_TITLE_FALLBACK = 'Integrated Early Warning and Response System'
  // The full-form title is the H1; the IEWRS abbreviation renders on its OWN
  // second row beneath it (client request — it must not sit inline at the end,
  // and editing the CMS title can never drop it in front of the sentence).
  // Strip any IEWRS the editor typed into the title so it isn't duplicated
  // across the two rows; the component owns the abbreviation's placement.
  const heroTitleBase = (content?.hero?.title || '').trim() || HERO_TITLE_FALLBACK
  const heroTitle = heroTitleBase.replace(/\(?\bIEWRS\b\)?/gi, '').replace(/\s{2,}/g, ' ').trim() || HERO_TITLE_FALLBACK
  const words = heroTitle
    .split(/\s+/)
    .filter(Boolean)
    .map(text => ({
      text,
      // Hero sits on the light landing gradient (see hero section below),
      // matching the original production look: dark navy base copy, primary
      // blue accent for the keywords.
      className: ACCENT_KEYWORDS.some(kw => text.includes(kw))
        ? 'text-primary-600 dark:text-primary-600'
        : 'text-gray-900 dark:text-gray-900'
    }))

  // Partners data with logos (using image overrides if available).
  // Source-of-truth: PARTNER_ROSTER (from /api/public/images). The
  // HARDCODED_PARTNERS_FALLBACK below is rendered only when the roster
  // is empty / API unreachable — so the page never blanks out, even
  // before any Image Manager save.
  //
  // Each entry carries a stable `id` (also the imageOverrides /
  // PARTNER_HARDCODED_LOGOS lookup key), and a `website` — the former
  // standalone "Important Links" section (Rwanda Space Agency, Rwanda
  // Biomedical Center, Rwanda Meteorology Agency) was folded into this
  // list rather than kept as a separate section, so a partner card with
  // a `website` renders as a link to that org's site and one without
  // renders as a plain (non-link) card, same defensive fallback the
  // logo itself already had.
  const HARDCODED_PARTNERS_FALLBACK = [
    {
      id: 'rwanda_space_agency',
      name: "Rwanda Space Agency",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.rwanda_space_agency, "/logos/Rwanda Space Agency Logo.png"),
      description: "Leading space technology and satellite monitoring for climate data",
      website: "https://www.rsa.rw",
    },
    {
      id: 'rwanda_biomedical_center',
      name: "Rwanda Biomedical Center",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.rwanda_biomedical_center, "/logos/Rwanda Biomedical Center Logo.png"),
      description: "National center for disease surveillance and health response",
      website: "https://www.rbc.gov.rw",
    },
    {
      id: 'university_rwanda',
      name: "University of Rwanda",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.university_rwanda, "/logos/University of Rwanda Log.png"),
      description: "Research excellence in health and environmental sciences",
      website: "https://www.ur.ac.rw",
    },
    {
      id: 'mount_kigali_university',
      name: "Mount Kigali University",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.mount_kigali_university, "/logos/Mount Kigali University Logo.png"),
      description: "Innovation in health technology and data science",
      website: "https://mkur.ac.rw",
    },
    {
      id: 'aims_ric',
      name: "AIMS-RIC",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.aims_ric, null),
      description: "African Institute for Mathematical Sciences Research and Innovation Centre — mathematical modeling excellence",
      website: "https://aimsric.org/",
    },
    {
      id: 'uganda_meteorological_authority',
      name: "Uganda National Meteorological Authority",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.uganda_meteorological_authority, null),
      description: "Meteorological services supporting regional climate-health collaboration",
      website: "https://meteo.mwe.go.ug/",
    },
    {
      id: 'meteo_benin',
      name: "Meteo Benin",
      logo: resolveImageOverride(imageOverrides.partnerLogos?.meteo_benin, "/logos/Agence Nationale de la Meteorologie du Benin Logo.png"),
      description: "Meteorological expertise and climate monitoring",
      website: "https://www.meteobenin.bj",
    },
  ]

  // Hardcoded logo fallbacks, keyed by roster id. Used when a partner
  // is added via the Image Manager without uploading a logo yet.
  const PARTNER_HARDCODED_LOGOS = {
    rwanda_space_agency:             "/logos/Rwanda Space Agency Logo.png",
    rwanda_biomedical_center:        "/logos/Rwanda Biomedical Center Logo.png",
    university_rwanda:               "/logos/University of Rwanda Log.png",
    mount_kigali_university:         "/logos/Mount Kigali University Logo.png",
    meteo_benin:                     "/logos/Agence Nationale de la Meteorologie du Benin Logo.png",
    aims_ric:                        null,
    uganda_meteorological_authority: null,
  }

  // Final partner list — roster-driven if the API returned a non-empty
  // roster, otherwise the hardcoded fallback so an API failure never
  // empties the section. `website` passes straight through from the
  // roster (services/auth/routes/public.js's DEFAULT_PARTNER_ROSTER /
  // whatever an admin saved via the Image Manager) — undefined/empty
  // resolves to null, same "renders as a non-link" fallback as the
  // hardcoded list. Never sliced/capped: every active roster entry
  // renders, however many that is.
  const partners =
    (apiPartnerRoster && apiPartnerRoster.length > 0)
      ? apiPartnerRoster
          .filter(p => p.isActive !== false)
          .map(p => ({
            id:          p.id,
            name:        p.name,
            description: p.description || '',
            logo:        resolveImageOverride(imageOverrides.partnerLogos?.[p.id], PARTNER_HARDCODED_LOGOS[p.id]),
            website:     p.website || null,
          }))
      : HARDCODED_PARTNERS_FALLBACK

  // Framework diagram — the one standalone (not-tied-to-a-team-member-or-
  // partner) image an admin can edit, via Settings → Marketing Images'
  // "Page graphics" tab. Source of truth: imageOverrides.sectionImages.framework
  // (⇄ POST/GET /api/public/images, same store as team/partner photos).
  // The bundled /images/iewrs-framework.png + its .webp companion remain the
  // default shown until an admin uploads a replacement — same "hardcoded
  // fallback, never blank" pattern as every other image on this page.
  const DEFAULT_FRAMEWORK_IMAGE = '/images/iewrs-framework.png'
  const frameworkImage = resolveSectionImage(imageOverrides.sectionImages, 'framework', DEFAULT_FRAMEWORK_IMAGE)
  const hasCustomFrameworkImage = frameworkImage !== DEFAULT_FRAMEWORK_IMAGE

  // Real system counts from landingAPI.getSystemMetrics(). Until 2026-07 every
  // value here was scaled to a tenth (* 0.1, commented "reduced by 90% for
  // better demo") and fell back to invented round numbers when the API gave
  // nothing — so the public site of a Ministry system showed figures that were
  // neither the real value nor derived from anything. Both are gone: the real
  // count is shown as-is, and an unavailable metric renders as a dash (handled
  // in MetricCard) rather than a fabricated number. What should display if the
  // metrics API is down is a content decision for the Ministry, not a default
  // to invent here.
  const asMetric = (value) => (Number.isFinite(value) ? Math.floor(value) : null)
  const metrics = [
    {
      title: "AI Models",
      value: asMetric(systemMetrics.models),
      icon: Brain,
      description: "Advanced machine learning models for health prediction"
    },
    {
      title: "Active Users",
      value: asMetric(systemMetrics.users),
      icon: Users,
      description: "Researchers and health experts using the platform"
    },
    {
      title: "Health Alerts",
      value: asMetric(systemMetrics.alerts),
      icon: Activity,
      description: "Early warnings generated and delivered"
    },
    {
      title: "Data Points",
      value: asMetric(systemMetrics.dataPoints),
      icon: Database,
      description: "Climate and health data points processed"
    }
  ]


  // Final team list — comes from /api/public/team (users where
  // show_on_landing=true). Stage 7 (06 June 2026) removed the legacy
  // marketing-images.json team fallback. If the API returns nothing the
  // section renders empty — the admin enables users in /app/settings/people/users.
  const teamMembers = (apiPublicTeam || []).map(m => ({
    ...m,
    // m.image is now a path to GET /api/public/team/:id/photo (was an
    // inlined base64 data URL) — an <img src> works identically either
    // way. Keep the imageOverrides map as one more layer for very old
    // saves (harmless when empty).
    image: resolveImageOverride(m.image, imageOverrides.teamImages?.[m.id]),
  }))

  // Two-tier split for the "Expert Team" section — see teamCardLogic.mjs.
  // Derived from member.bio, not from names/order, so this stays correct
  // (and collapses to a single tier with zero code changes) once the
  // client supplies the write-ups still missing today.
  const teamSectionLayout = resolveTeamSectionLayout(teamMembers)

  // Handle team member selection. Stores the triggering card so keyboard
  // focus can be returned to it when the modal closes, instead of being
  // dropped back at document.body.
  const handleTeamMemberClick = (member, event) => {
    teamModalTriggerRef.current = event?.currentTarget || null
    setSelectedTeamMember(member)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedTeamMember(null)
    teamModalTriggerRef.current?.focus()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white overflow-x-hidden">
      <LandingNavbar />
      <main>
      {/* Hero Section — full viewport height (accounting for the fixed navbar
          via top padding), content vertically centered. Light landing wash
          (the original production look) rather than the deep-blue gradient.
          No floating data-card/particle background — client asked for a
          clean hero with nothing overlapping the headline. */}
      <motion.section
        id="hero"
        className="relative isolate flex min-h-viewport items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-blue-50 to-white pt-24 pb-20 md:pt-28 md:pb-24"
        style={{ opacity, scale }}
      >
        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          {/* The official IEWRS logo now lives in the top navbar (wordmark) and
              the footer (full lockup) — the hero leads with the animated
              headline instead of a large static logo block. */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-8"
          >
            {/* `as="h1"` makes the visible hero title the page's one <h1>,
                instead of adding a separate sr-only heading — the page
                previously started at h2 with no h1 at all. */}
            <TypewriterEffectSmooth words={words} as="h1" />
            {/* IEWRS abbreviation on its own second row beneath the full-form
                title (client request) — a deliberate lockup that's always here
                regardless of the CMS title text. */}
            <p className="mt-2 text-center text-3xl md:text-4xl font-extrabold tracking-tight text-gradient">IEWRS</p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 mb-0 max-w-2xl mx-auto leading-relaxed font-normal"
          >
            {content?.hero?.subtitle ||
              `Develop and implement an integrated system to identify, predict and respond to climate-related health diseases and challenges through advanced AI and real-time monitoring`}
          </motion.p>
        </div>

        {/* Scroll cue — a plain wayfinding affordance (no added copy), points
            at the next existing section. */}
        <motion.a
          href="#objectives"
          onClick={(e) => {
            e.preventDefault()
            const el = document.getElementById('objectives')
            if (!el) return
            const top = el.getBoundingClientRect().top + window.pageYOffset - 88
            window.scrollTo({ top, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
          }}
          aria-label="Scroll to Project Objectives"
          className="absolute bottom-8 left-1/2 z-10 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border border-gray-300 bg-white/60 text-gray-500 backdrop-blur-sm transition-colors hover:bg-white/90 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-0"
          animate={prefersReducedMotion ? undefined : { y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="h-5 w-5" strokeWidth={1.75} />
        </motion.a>
      </motion.section>

      {/* Project Objectives Section */}
      <section id="objectives" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            maxWidthClass="max-w-4xl"
            title={content?.objectives?.section_title || 'Project Objectives'}
            subtitle={content?.objectives?.section_description || (
              <>
                Targeting four climate-sensitive diseases — <span className="font-semibold text-primary-600">malaria</span>, <span className="font-semibold text-primary-600">flu syndrome</span>, <span className="font-semibold text-primary-600">pneumonia</span>, and <span className="font-semibold text-primary-600">diarrhea</span> — across
                all 416 sectors of Rwanda, the IEWRS project pursues three core objectives.
              </>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(
              // Read from editable content when available. Map the icon
              // string back to the lucide component, with a safe fallback
              // to Brain if the editor stored an unknown name.
              (content?.objectives?.items?.length ? content.objectives.items.map(it => ({
                icon: ({ Brain, Database, Target, Heart, Shield, Activity, Users, Globe, Wind, Thermometer, Droplets, Leaf, Factory, TrendingUp, AlertTriangle, Mail, Smartphone, Monitor }[it.icon] || Brain),
                title: it.title,
                description: it.description,
              })) : [
                {
                  icon: Brain,
                  title: "Diagnostic Tools & Predictive Models",
                  description: "Developing advanced diagnostic tools, technology and predictive models for disease outbreak detection based on climate data patterns, enabling timely interventions."
                },
                {
                  icon: Database,
                  title: "Data Acquisition Platform",
                  description: "A continuous data collection platform ensuring comprehensive monitoring of climate and health indicators for health adaptation actions implementation."
                },
                {
                  icon: Target,
                  title: "Resource Allocation & Response",
                  description: "Enabling timely resource allocation and rapid response through automated alert systems at web-based, email, and mobile phone levels."
                }
              ])
            ).map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-gray-200/80 shadow-elev-1 hover:shadow-elev-2 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50">
                  <objective.icon className="w-7 h-7 text-primary-600" strokeWidth={1.75} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{objective.title}</h3>
                <p className="text-gray-600 leading-relaxed">{objective.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IEWRS Framework Section */}
      <section id="framework" className="py-24 bg-gradient-to-r from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            className="mb-16"
            title={content?.framework?.section_title || 'IEWRS Framework'}
            subtitle={content?.framework?.section_description ||
              'Four key pillars driving climate-health intelligence and early warning capabilities'}
          />

          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:w-1/2"
            >
              {hasCustomFrameworkImage ? (
                // Admin-uploaded override — no bundled .webp companion for
                // an arbitrary upload, so no <picture>/<source> pair here.
                <img
                  src={frameworkImage}
                  alt="IEWRS Framework - Four pillars of climate-health intelligence"
                  className="rounded-2xl shadow-elev-2 border border-gray-200/80 w-full"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              ) : (
                <picture>
                  <source srcSet="/images/iewrs-framework.webp" type="image/webp" />
                  <img
                    src="/images/iewrs-framework.png"
                    alt="IEWRS Framework - Four pillars of climate-health intelligence"
                    className="rounded-2xl shadow-elev-2 border border-gray-200/80 w-full"
                    width="1280"
                    height="817"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </picture>
              )}
            </motion.div>

            <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: AlertTriangle, title: "Risk Knowledge", description: "Climate information and risks that guide early warning system (IEWRS)", color: "from-red-500 to-orange-500" },
                { icon: Monitor, title: "Monitoring & Warning", description: "Improved delivery of climate-related data through continuous monitoring", color: "from-yellow-500 to-amber-500" },
                { icon: Mail, title: "Communication", description: "Effective and timely communication that reaches communities at risk", color: "from-blue-500 to-cyan-500" },
                { icon: Shield, title: "Response Capability", description: "Preparedness, response plans and public awareness for health emergencies", color: "from-green-500 to-emerald-500" }
              ].map((pillar, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-elev-1 hover:shadow-elev-2 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${pillar.color} flex items-center justify-center mb-4`}>
                    <pillar.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{pillar.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{pillar.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Alert System Levels */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-200/80 shadow-elev-1"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Alert System Levels</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: Monitor, label: "Web-Based Alerts", desc: "Real-time dashboard notifications" },
                { icon: Mail, label: "Email Alerts", desc: "Targeted health worker notifications" },
                { icon: Smartphone, label: "Mobile Messages", desc: "SMS alerts to communities at risk" }
              ].map((alert, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-elev-1 border border-gray-200/80 transition-shadow duration-300 hover:shadow-elev-2">
                  <alert.icon className="w-5 h-5 text-primary-600" />
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{alert.label}</p>
                    <p className="text-xs text-gray-500">{alert.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Health Data Section */}
      <section id="health-data" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            title={content?.diseases?.section_title || 'Climate-Sensitive Diseases'}
            subtitle={content?.diseases?.section_description ||
              'Monthly health data across 416 sectors of Rwanda used for disease prediction and early warning'}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(
              // Disease cards — name/description come from
              // resolveDiseaseCopy() (landingContentDefaults.js): a saved
              // CMS override (.diseases.items[key]) wins per-field, but an
              // empty/missing CMS item (e.g. after a rebuild that didn't
              // carry the file-backed content store over) falls back to
              // the durable code default instead of rendering blank. Icon
              // and the visual styling (color, bgColor) stay hardcoded
              // here so the palette stays in sync across the page even
              // when names change.
              ['malaria', 'flu', 'pneumonia', 'diarrhea'].map((key, i) => {
                const STYLES = [
                  { color: 'from-red-500 to-rose-600',    bgColor: 'bg-red-50' },
                  { color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50' },
                  { color: 'from-purple-500 to-violet-600', bgColor: 'bg-purple-50' },
                  { color: 'from-amber-500 to-orange-600',  bgColor: 'bg-amber-50' },
                ]
                const HARDCODED_ICONS = { malaria: '🦟', flu: '🦠', pneumonia: '🫁', diarrhea: '💧' }
                const fromContent = content?.diseases?.items?.[key]
                const diseaseCopy = resolveDiseaseCopy(key, fromContent)
                const icon = (fromContent && typeof fromContent.icon === 'string' && fromContent.icon.trim())
                  ? fromContent.icon
                  : HARDCODED_ICONS[key]
                return { ...diseaseCopy, icon, ...STYLES[i] }
              })
            ).map((disease, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`${disease.bgColor} rounded-2xl p-6 border border-gray-200/80 shadow-elev-1 hover:shadow-elev-2 transition-all duration-300`}
              >
                <div className="text-4xl mb-4">{disease.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{disease.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{disease.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Climate Data Section */}
      <ClimateDataSection content={content} />

      {/* Rwanda Map & Case Study Section */}
      <section id="case-study" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            className="mb-12"
            title="Case Study: 416 Sectors Across Rwanda"
            subtitle="Comprehensive coverage of all administrative sectors with health facility mapping"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <picture>
              <source srcSet="/images/rwanda-map.webp" type="image/webp" />
              <img
                src="/images/rwanda-map.png"
                alt="Administrative map of Rwanda showing all sectors and health facilities"
                className="rounded-2xl shadow-elev-2 border border-gray-200/80 max-w-4xl w-full"
                width="1792"
                height="857"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            </picture>
          </motion.div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-4 text-center border border-gray-200/80 shadow-elev-1">
              <p className="text-sm text-gray-500 mb-1">Administrative Map</p>
              <p className="text-lg font-semibold text-gray-900">5 Provinces, 416 Sectors</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4 text-center border border-gray-200/80 shadow-elev-1">
              <p className="text-sm text-gray-500 mb-1">Health Facilities</p>
              <p className="text-lg font-semibold text-gray-900">Nationwide Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Results Section */}
      <section id="results" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            className="mb-12"
            title="Research Results"
            subtitle="Malaria prediction models showing observed vs. forecasted cases using multiple machine learning approaches"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div className="bg-white rounded-2xl shadow-elev-2 border border-gray-200/80 p-6 max-w-4xl w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                Malaria Forecast — Observed vs. Predicted Cases
              </h3>
              <picture>
                <source srcSet="/images/malaria-forecast.webp" type="image/webp" />
                <img
                  src="/images/malaria-forecast.png"
                  alt="Malaria forecast chart showing observed vs predicted cases using RandomForest, XGBoost, and CatBoost models"
                  className="w-full rounded-lg"
                  width="1660"
                  height="885"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </picture>
              <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-black inline-block"></span> Observed</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block"></span> RandomForest</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 inline-block"></span> XGBoost</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-500 inline-block"></span> CatBoost</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Target Audience */}
      <section id="audience" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            title={content?.audiences?.section_title || 'Built for Health Leaders'}
            subtitle={content?.audiences?.section_description ||
              'Designed for researchers, data scientists, health experts, and stakeholders working at the intersection of climate and health'}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(
              (content?.audiences?.items?.length ? content.audiences.items : [
                { title: "Researchers" },
                { title: "Data Scientists" },
                { title: "Health Experts" },
                { title: "Policy Makers" },
              ]).map((it, i) => {
                const STYLES = [
                  { icon: Brain,       color: 'from-blue-500 to-cyan-500' },
                  { icon: TrendingUp,  color: 'from-purple-500 to-pink-500' },
                  { icon: Heart,       color: 'from-green-500 to-emerald-500' },
                  { icon: Globe,       color: 'from-orange-500 to-red-500' },
                ]
                return { ...STYLES[i % STYLES.length], title: it.title || it.label || it.name || '' }
              })
            ).map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-6 rounded-2xl text-center shadow-elev-1 border border-gray-200/80 hover:shadow-elev-2 transition-all duration-300"
              >
                <audience.icon className="w-12 h-12 mx-auto mb-4 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">{audience.title}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Metrics */}
      <section id="metrics" className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          <AnimatedMetrics metrics={metrics} />
        </div>
      </section>

      {/* Partners Section — every institution the project works with,
          each card a link to that org's website (opens in a new tab)
          when one is on file, a plain (non-link) card otherwise — same
          defensive fallback the logo image already had. This also
          absorbed the former standalone "Important Links" section
          (Rwanda Space Agency / Rwanda Biomedical Center / Rwanda
          Meteorology Agency): those are partner cards now, not a
          separate footer block. `scroll-mt-24` keeps the section clear
          of the fixed navbar when reached via #partners (nav link /
          back-button / deep link). Never capped — every active roster
          entry renders as its own card, regardless of count. */}
      <section id="partners" className="scroll-mt-24 py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            title="Our Partners"
            subtitle="Collaborating with leading institutions to advance climate-health research and response"
          />

          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
            {partners.map((partner, index) => {
              // isSafeHttpUrl (landingContentDefaults.js) is a hard
              // requirement, not a nicety: `partner.website` is admin-
              // supplied (POST /api/public/images accepts a full
              // partnerRoster replace from anyone holding manage_images)
              // and renders as a live <a href> for every anonymous public
              // visitor. Without the http(s) allow-list here a
              // `javascript:`/`data:` "website" would be a stored-XSS
              // payload firing on click — the server enforces the same
              // rule independently (services/auth/routes/public.js), this
              // is defense in depth against a client bypass, not the only
              // gate.
              const hasWebsite = isSafeHttpUrl(partner.website)
              const CardTag = hasWebsite ? motion.a : motion.div
              const linkProps = hasWebsite
                ? {
                    href: partner.website.trim(),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    'aria-label': `${partner.name} — opens in a new tab`,
                  }
                : {}

              return (
                <CardTag
                  key={partner.id || partner.name || index}
                  {...linkProps}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: (index % 8) * 0.06 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -4 }}
                  className={cn(
                    'group flex flex-col items-center rounded-2xl border border-gray-200/80 bg-white p-6 text-center shadow-elev-1 transition-all duration-300 hover:shadow-elev-2',
                    hasWebsite && 'hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2'
                  )}
                >
                  <div className="relative mb-3 h-16 w-16 overflow-hidden rounded-xl bg-gradient-to-br from-primary-100 to-primary-200">
                    <div className="flex h-full w-full items-center justify-center">
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className="h-full w-full object-contain p-1"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextElementSibling.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <span
                        className="flex h-full w-full items-center justify-center text-sm font-bold text-primary-600"
                        style={{ display: partner.logo ? 'none' : 'flex' }}
                      >
                        {partner.name.split(' ').map(word => word[0]).join('').slice(0, 3)}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-xs font-semibold leading-tight text-gray-900">
                    {partner.name}
                  </h3>

                  {hasWebsite && (
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 transition-colors group-hover:text-primary-700">
                      Visit website
                      <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  )}
                </CardTag>
              )
            })}
          </div>
        </div>
      </section>

      {/* Team Section — two-tier layout: "featured" members with a full
          biography (larger card, bio excerpt, "View full profile"), and a
          quieter "Project team" tier for everyone else (photo, name,
          institution, role if present). The split is derived from the
          data (member.bio present or not — see teamCardLogic.mjs), never
          from a hardcoded name list or landing order, so once the client
          supplies the remaining write-ups this collapses to one uniform
          tier automatically. Both tiers use the same
          `flex flex-wrap justify-center` wrapping strategy (just
          different card sizes), which is what keeps this reading as one
          section instead of two designs bolted together, and is what
          centers any ragged final row regardless of how many members end
          up in either tier. */}
      <section id="team" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            title="Expert Team"
            subtitle="Led by distinguished researchers and practitioners in climate science and public health"
          />

          {teamSectionLayout.showFeaturedTier && (
            <div className="flex flex-wrap justify-center gap-6">
              {teamSectionLayout.featured.map((member, index) => (
                <FeaturedTeamCard
                  key={member.id ?? member.name ?? index}
                  member={member}
                  index={index}
                  prefersReducedMotion={prefersReducedMotion}
                  onSelect={handleTeamMemberClick}
                />
              ))}
            </div>
          )}

          {/* "Project team" divider — only rendered when BOTH tiers have
              members. If everyone has a bio (rest is empty) or nobody does
              (featured is empty), this heading would sit over nothing, so
              teamCardLogic.mjs's showRestHeading is false in both of those
              cases and the section below renders as a single plain tier. */}
          {teamSectionLayout.showRestHeading && (
            <div className="mt-16 mb-8 flex items-center gap-4" role="presentation">
              <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                Project team
              </h3>
              <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
            </div>
          )}

          {teamSectionLayout.showRestTier && (
            <div className="flex flex-wrap justify-center gap-4">
              {teamSectionLayout.rest.map((member, index) => (
                <CompactTeamCard
                  key={member.id ?? member.name ?? index}
                  member={member}
                  index={index}
                  prefersReducedMotion={prefersReducedMotion}
                  onSelect={handleTeamMemberClick}
                />
              ))}
            </div>
          )}

          {/* Trained PhD Students — people the project trained, not project
              team members. Kept under the same "Expert Team" heading as a
              distinct tier (client, 18 Aug 2026). Category-driven, so it
              stays empty and invisible until members are assigned. */}
          {teamSectionLayout.showTrainedPhdTier && (
            <>
              <div className="mt-16 mb-8 flex items-center gap-4" role="presentation">
                <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
                  Trained PhD Students
                </h3>
                <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                {teamSectionLayout.trainedPhdStudents.map((member, index) => (
                  <CompactTeamCard
                    key={member.id ?? member.name ?? index}
                    member={member}
                    index={index}
                    prefersReducedMotion={prefersReducedMotion}
                    onSelect={handleTeamMemberClick}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <section id="cta" className="py-24 bg-gradient-to-r from-primary-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {content?.cta?.title || 'Ready to Join the Future of Climate-Health Intelligence?'}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {content?.cta?.subtitle ||
                'Access advanced AI tools and collaborate with leading researchers to make data-driven decisions for health protection.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/request-access">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-primary-600 px-8 py-4 rounded-full font-semibold shadow-elev-2 hover:bg-gray-100 hover:shadow-elev-3 transition-all duration-300 flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-600"
                >
                  Request Access
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <Link to="/login">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-transparent border-2 border-white/80 text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-primary-600 hover:border-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-600"
                >
                  Sign In
                </motion.button>
              </Link>
              {/* "Manage Images" button removed 06 June 2026 — image
                  management now lives behind the Settings sidebar after
                  login, so it doesn't belong as a top-level public CTA. */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Funding & Grantor — grant acknowledgment (client, 18 Aug 2026).
          Restrained institutional plate: grantor mark, funder line, the
          reference number as a copy-friendly chip, and the grant title.
          NCST logo falls back to a text mark if the asset isn't on disk
          yet — same defensive onError pattern the partner logos use, so
          the section never shows a broken image. */}
      <section id="funding" className="scroll-mt-24 border-t border-gray-100 bg-white py-24">
        <div className="mx-auto max-w-3xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-col items-center rounded-2xl border border-gray-200/80 bg-gray-50 px-6 py-12 text-center shadow-elev-1 sm:px-12"
          >
            <span className="mb-6 text-xs font-semibold uppercase tracking-widest text-gray-400">
              Funding &amp; Grantor
            </span>

            <div className="relative mb-6 flex h-20 w-40 items-center justify-center">
              <img
                src="/logos/NCST Logo.png"
                alt="National Council for Science and Technology (NCST)"
                className="h-full w-full object-contain"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.nextElementSibling.style.display = 'flex'
                }}
              />
              <span
                className="absolute inset-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-lg font-bold text-primary-700"
                style={{ display: 'none' }}
              >
                NCST
              </span>
            </div>

            <p className="max-w-2xl text-base leading-relaxed text-gray-700">
              This project is funded by the{' '}
              <span className="font-semibold text-gray-900">
                National Council for Science and Technology (NCST)
              </span>{' '}
              through the{' '}
              <span className="font-semibold text-gray-900">
                Bill &amp; Melinda Gates Foundation
              </span>
              , for grant funding under{' '}
              <span className="italic text-gray-900">
                “Accelerating and Catalyzing Solutions for Climate Change&apos;s
                Impact on Health (GC Rwanda Climate for Health).”
              </span>
            </p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                Reference No
              </span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                202401310004
              </span>
            </div>
          </motion.div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="py-6 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm mb-1">
            Powered by{' '}
            <a
              href="https://www.streamlinexperts.rw"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
            >
              SLX - Streamlinexperts
            </a>
          </p>
          <p className="text-gray-500 text-xs">
            {content?.footer?.copyright ||
              '© 2025 IEWRS. Advancing climate-health intelligence for a safer future.'}
          </p>
        </div>
      </footer>

      {/* The bottom floating pill (FloatingNavigation) has been retired in
          favour of the sticky top navbar above — one wayfinding surface
          instead of two competing ones. FloatingNavigation.jsx is untouched
          and still available if a future design wants it back. */}

      {/* Team Member Modal */}
      <TeamMemberModal
        member={selectedTeamMember}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  )
}

export default LandingPage